'use client'

import { createContext, useContext, useEffect, useState } from "react"
import { apiFetch } from "./api"

const API_BASE = (process.env.NEXT_PUBLIC_API_URL || 'http://localhost:8001/api').replace(/\/$/, '')

type User = {
  id: number
  email: string
}

type AuthContextType = {
  user: User | null
  loading: boolean
  login: (identifier: string, password: string) => Promise<boolean>
  logout: () => Promise<void>
  refreshUser: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | null>(null)

export function AuthProvider({ children }: { children: React.ReactNode }) {

  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)

  async function refreshUser() {
    try {
      const res = await apiFetch(`${API_BASE}/auth/me/`)
      if (!res.ok) throw new Error()

      const data = await res.json()
      setUser(data.user)
    } catch {
      setUser(null)
    } finally {
      setLoading(false)
    }
  }

  async function login(identifier: string, password: string) {

    const res = await fetch(
      `${API_BASE}/auth/login/`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ identifier, password })
      }
    )

    if (!res.ok) return false

    await refreshUser()
    return true
  }

  async function logout() {
    try {
      await fetch(
        `${API_BASE}/auth/logout/`,
        {
          method: "POST",
          credentials: "include"
        }
      )
    } catch {
      // Even if the request fails (network, server down), we still clear local state
      // so the user gets out of the authenticated UI.
    }

    setUser(null)

    // Best-effort client-side cookie wipe (cas cross-origin où l'expire serveur
    // n'a pas pris). `httpOnly` cookies ne peuvent pas être touchés ici, mais
    // toute autre persistance auth doit dégager.
    if (typeof document !== "undefined") {
      for (const name of ["access_token", "refresh_token"]) {
        document.cookie = `${name}=; Max-Age=0; path=/`
      }
    }
  }

  useEffect(() => {
    refreshUser()
  }, [])

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, refreshUser }}>
        {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider")
  return ctx
}